import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMasterDataMapping, useUpdateMasterDataMapping } from "@/hooks/use-master-data-mappings";
import { MasterDataMappingForm } from "./MasterDataMappingForm";

interface MasterDataMappingEditDialogProps {
  id: string | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Edit dialog — fetches fresh data via `useMasterDataMapping(id, { enabled:
 * isOpen })` per `.claude/rules/frontend.md`'s Dialogs rule, wraps the same
 * `MasterDataMappingForm` used by create.
 */
export function MasterDataMappingEditDialog({
  id,
  onOpenChange,
}: MasterDataMappingEditDialogProps) {
  const isOpen = id !== null;
  const { data, isLoading } = useMasterDataMapping(id ?? "", { enabled: isOpen });
  const updateMapping = useUpdateMasterDataMapping(id ?? "");

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit mapping</DialogTitle>
          <DialogDescription>Update this master-data mapping.</DialogDescription>
        </DialogHeader>
        {isLoading || !data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <MasterDataMappingForm
            key={data.id}
            entityType={data.entityType}
            submitLabel="Save"
            isSaving={updateMapping.isPending}
            defaultValues={{
              sourceBuId: data.sourceBuId,
              targetBuId: data.targetBuId,
              sourceRefId: data.sourceRefId,
              targetRefId: data.targetRefId,
              isActive: data.isActive,
            }}
            onCancel={() => onOpenChange(false)}
            onSubmit={(values) =>
              updateMapping.mutate(values, { onSuccess: () => onOpenChange(false) })
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
